import React, { useEffect, useState } from "react";
import {
  getWithTokenAPI,
  putAPI,
  deleteAPI,
} from "../../utils/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
  import Swal from "sweetalert2";
import {
  CircularProgress,
  Box,
  Typography,
  Pagination,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  DialogContentText,
} from "@mui/material";
import {
  ReceiptLong as ReceiptLongIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupIcon from "@mui/icons-material/Group";

const EnquiryService = () => {
  const [enquiryData, setEnquiryData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Dialog states
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    location: "",
    enquiry_for: "",
    additional_info: "",
    status: "Active",
    _id: "",
  });

  // Colors
  const primaryGreen = "#DC3545";
  const lightGreen = "#ffe5e5";
  const secondaryTextColor = "rgba(0, 0, 0, 0.6)";

  // Fetch Enquiries
  const fetchEnquiries = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const res = await getWithTokenAPI(token, "/enquiry/list");
      if (res.statusCode === 200) {
        setEnquiryData(res.data.enquiries || []);
      } else {
        toast.error(res.message || "Failed to load enquiries.");
      }
    } catch (error) {
      console.error("Fetch enquiries error:", error);
      toast.error("Network error while loading enquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  // Pagination logic
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentEnquiries = enquiryData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(enquiryData.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Open dialogs
  const handleOpenEdit = (item) => {
    setFormData(item);
    setOpenEditDialog(true);
  };

  const handleOpenView = (item) => {
    setSelectedItem(item);
    setOpenViewDialog(true);
  };

  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  // Close dialogs
  const handleCloseAll = () => {
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setOpenViewDialog(false);
  };

  // Save / Update
const handleSave = async () => {
  const token = localStorage.getItem("token");
  try {
    const res = await putAPI(token, `/enquiry/update`, formData);

    if (res.statusCode === 200) {
      // Success alert
      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "Enquiry updated successfully! ",
        showConfirmButton: false,
        timer: 1500,
      });

      fetchEnquiries();   // Refresh data
      handleCloseAll();   // Close dialog
    } else {
      // Error alert
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: res.message || "Failed to update enquiry.",
        confirmButtonColor: "#d33",
      });
    }
  } catch (error) {
    console.error("Save error:", error);
    Swal.fire({
      icon: "error",
      title: "Something went wrong!",
      text: "Please try again later.",
      confirmButtonColor: "#d33",
    });
  }
};


  // Delete confirm
  const handleConfirmDelete = async () => {
    const token = localStorage.getItem("token");
    const payload = { _id: deleteId };
    try {
      const res = await deleteAPI(token, `/enquiry/delete`, payload);
      if (res.statusCode === 200) {
        toast.success("Enquiry deleted successfully!");
        fetchEnquiries();
      } else {
        toast.error(res.message || "Failed to delete enquiry.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Something went wrong while deleting.");
    } finally {
      handleCloseAll();
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ToastContainer position="top-center" autoClose={1500} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <ReceiptLongIcon sx={{ color: primaryGreen, fontSize: 50, mr: 2 }} />
        <Box>
          <Typography variant="h4" sx={{ color: primaryGreen, fontWeight: "bold" }}>
            Services Enquiry
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Manage your user enquiries efficiently
          </Typography>
        </Box>
      </Box>

      {/* Table */}
      <div className="table-container border rounded">
        <div className="table-responsive">
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
              <CircularProgress style={{ color: "#DC3545" }} />
              <Typography sx={{ ml: 2, color: "#DC3545" }}>Loading enquiries...</Typography>
            </Box>
          ) : (
            <table className="table m-0 resource-head">
              <thead style={{ backgroundColor: lightGreen }}>
                <tr>
                  <th style={{ color: secondaryTextColor }}>S.No</th>
                  <th style={{ color: secondaryTextColor }}>Name</th>
                  <th style={{ color: secondaryTextColor }}>Email</th>
                  <th style={{ color: secondaryTextColor }}>Phone</th>
                  <th style={{ color: secondaryTextColor }}>Location</th>
                  <th style={{ color: secondaryTextColor }}>Enquiry For</th>
                  {/* <th style={{ color: secondaryTextColor }}>Status</th> */}
                  <th style={{ textAlign: "center", color: secondaryTextColor }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentEnquiries.length > 0 ? (
                  currentEnquiries.map((item, index) => (
                    <tr key={item._id}>
                      <td>{indexOfFirst + index + 1}</td>
                      <td>{item.first_name} {item.last_name}</td>
                      <td>{item.email}</td>
                      <td>{item.phone_number}</td>
                      <td>{item.location}</td>
                      <td>{item.enquiry_for}</td>
                      {/* <td>{item.status}</td> */}
                      <td>
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                          <IconButton onClick={() => handleOpenView(item)} sx={{ color: primaryGreen }}>
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton onClick={() => handleOpenEdit(item)}>
                            <EditIcon sx={{ color: primaryGreen }} />
                          </IconButton>
                          <IconButton onClick={() => handleOpenDeleteDialog(item._id)}>
                            <DeleteIcon sx={{ color: "#d32f2f" }} />
                          </IconButton>
                        </Box>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">
                      <GroupIcon sx={{ fontSize: 60, color: "text.secondary" }} />
                      <Typography variant="h6" sx={{ color: "text.secondary" }}>
                        No enquiries found.
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "end", mt: 3, pb: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": { color: primaryGreen },
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: primaryGreen,
                color: "white",
              },
            }}
          />
        </Box>
      )}

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseAll} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            fontWeight: "bold",
            color: primaryGreen,
            fontSize: "1.5rem",
            borderBottom: `2px solid ${primaryGreen}`,
            pb: 1,
          }}
        >
          View Enquiry Details
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            backgroundColor: "#f9f9f9",
            px: 3,
            py: 2,
          }}
        >
          {selectedItem && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 2,
              }}
            >
              {[
                { label: "First Name", value: selectedItem.first_name },
                { label: "Last Name", value: selectedItem.last_name },
                { label: "Email", value: selectedItem.email },
                { label: "Phone Number", value: selectedItem.phone_number },
                { label: "Location", value: selectedItem.location },
                { label: "Enquiry For", value: selectedItem.enquiry_for },
                { label: "Additional Info", value: selectedItem.additional_info },
                { label: "Status", value: selectedItem.status },
              ].map((field) => (
                <Box
                  key={field.label}
                  sx={{
                    backgroundColor: "white",
                    p: 2,
                    borderRadius: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    borderLeft: `4px solid ${primaryGreen}`,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: primaryGreen,
                      fontWeight: "bold",
                      mb: 0.5,
                      textTransform: "uppercase",
                      fontSize: "13px",
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: secondaryTextColor,
                      fontSize: "14px",
                    }}
                  >
                    {field.value || "-"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", px: 3, py: 2 }}>
          <Button
            onClick={handleCloseAll}
            sx={{
              backgroundColor: "#d32f2f",
              color: "white",
              textTransform: "none",
              px: 3,
              "&:hover": { backgroundColor: "#b71c1c" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseAll} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: "bold", color: primaryGreen }}>Edit Enquiry</DialogTitle>
        <DialogContent dividers>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {[
              { label: "First Name", name: "first_name" },
              { label: "Last Name", name: "last_name" },
              { label: "Email", name: "email" },
              { label: "Phone Number", name: "phone_number" },
              { label: "Location", name: "location" },
              { label: "Enquiry For", name: "enquiry_for" },
              { label: "Additional Info", name: "additional_info" },
            ].map((field) => (
              <li key={field.name} style={{ marginBottom: "12px" }}>
                <TextField
                  fullWidth
                  label={field.label}
                  name={field.name}
                  value={formData[field.name] || ""}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                />
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAll} color="error">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            sx={{
              backgroundColor: primaryGreen,
              color: "white",
              "&:hover": { backgroundColor: "#8abf2d" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={openDeleteDialog} onClose={handleCloseAll} fullWidth maxWidth="xs">
        <DialogTitle sx={{ color: "#d32f2f", fontWeight: "bold" }}>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Are you sure you want to delete this enquiry? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAll} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              backgroundColor: "#d32f2f",
              color: "white",
              "&:hover": { backgroundColor: "#b71c1c" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnquiryService;
